<template>
  <div
    data-component="InsuranceAnotherCompanyData"
    class="insurance-another-company-data"
  >
    <h2 class="gl_form-title small">
      {{ title }}
    </h2>

    <CardInfoComponent
      v-if="message"
      :icon="message.icon"
    >
      {{ message.label }}
    </CardInfoComponent>

    <FormField
      v-if="needsBirthdate"
      :statement="birthDateLabel"
      class="birthdate-form-field"
    >
      <BirthDatePickerComponent
        :legend="t('rater.common.more-info.birth-date.legend')"
        id="birthDate"
        v-model="birthDate"
        :rules="[validationRules.required, validationRules.tooYoung, validationRules.tooOld]"
      />
    </FormField>

    <BaseSelect
      id="country"
      v-model="country"
      :items="countries"
      :label="nationalityLabel"
    />

    <BaseSelect
      id="documentType"
      v-model="documentType"
      :items="availableDocuments"
      :label="t('hiring.common.insurance-another-company-data.document-type')"
      :rules="[validationRules.required]"
    />

    <BaseInput
      id="document-type"
      :key="documentType"
      :label="documentLabel"
      :placeholder="documentNumberLabel"
      v-model="documentNumber"
      icon="icon-card-01"
      :hide-details="true"
      :rules="documentNumberRules"
      :editable="documentType !== 'MENOR'"
      :uppercase="true"
    />
    <template v-if="documentType === 'PAS'">
      <label
        class="expirationDate"
        for="expirationDate"
      >
        {{ t('hiring.common.insurance-another-company-data.expiration-date') }}
      </label>

      <PassportDatePickerComponent
        :legend="t('hiring.common.insurance-another-company-data.expiration-date')"
        id="expirationDate"
        v-model="expirationDate"
        :rules="[
          validationRules.required,
          validationRules.validateMinDate,
          validationRules.validateDate
        ]"
      />
    </template>

    <template v-if="currentInsured.role !== UserRoleEnum.INSURED && !holder.privacyPolicyConsent">
      <FoldingText
        class="mb-3"
        :text="t('copy_policies_description')"
      />

      <BaseCheckBox
        id="policies"
        v-model="privacyPolicyConsent"
        :label="t('copy_policies_check')"
        :rules="[validationRules.checkRequired]"
      />
    </template>

    <BaseCheckBox
      v-if="showCheck"
      id="declaration"
      v-model="declaration"
      :label="t('hiring.common.insurance-another-company.check-label')"
      :rules="[validationRules.checkRequired]"
    />
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, onBeforeMount, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import FoldingText from '@/modules/core/components/utils/FoldingText.vue'
  import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import PassportDatePickerComponent from '@/modules/core/components/formFields/PassportDatePickerComponent.vue'
  import { isSmaller } from '@/utils/date.utils'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import { UserRoleEnum } from '@/modules/core/models/enums'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import BirthDatePickerComponent from '@/modules/core/components/formFields/BirthDatePickerComponent.vue'
  import { MAX_CHARS_PASSPORT } from '@/modules/core/constants/common'
  import { getBranch, getBranchMaxAge } from '@/modules/core/utils/BranchUtils'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import {
    hiringAges,
    hiringMinAge,
    hiringMaxAge
  } from '@/modules/core/composables/minMaxAgeComposable'
  import SpanishBranches from '@/modules/core/models/SpanishBranches'

  const { t } = useI18n()
  const {
    required,
    tooOld,
    tooYoung,
    validCIF,
    validNIE,
    validNIF,
    validateDate,
    validPassport,
    dateHigherOrEquals,
    tooOldGreaterOrEqual
  } = useRules()
  const commonStore = useCommonStore()
  const loadingStore = useLoadingStore()

  const SPANISH_NATIONALITY = 'ESPA'

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    extra: Record<string, any>
  }
  const props: PropsInterface = withDefaults(defineProps<PropsInterface>(), {})

  const title = computed(() =>
    t(
      props.extra?.customTitle
        ? props.extra.customTitle
        : 'hiring.common.insurance-another-company-data.form-legend'
    )
  )

  const birthDateLabel = computed(() => {
    const i18n = isHolder.value ? 'me' : 'not-me-singular'

    return t(`rater.common.more-info.birth-date.title-${i18n}`)
  })

  const nationalityLabel = computed(() => {
    const i18n = isHolder.value ? 'holder' : 'not-holder'

    return t(`hiring.common.insurance-another-company-data.nationality.${i18n}`)
  })

  const documentLabel = computed(() => {
    const i18n = isHolder.value ? 'holder' : 'not-holder'
    const additionalText =
      documentType.value === 'PAS'
        ? ` ${t('hiring.common.insurance-another-company-data.document-digits')}`
        : ''

    return (
      t(`hiring.common.insurance-another-company-data.document-number.${i18n}`) +
      `<span class="info">${additionalText}</span>`
    )
  })

  const needsBirthdate = computed(
    () =>
      (currentInsured.role === UserRoleEnum.HOLDER_NOT_INSURED &&
        getBranch() !== SpanishBranches.travel) ||
      props.extra?.needsBirthdate
  )

  const currentInsured = new Insured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )

  const holder = new Insured(
    props.extra?.usePolicyHolderAsHolder ? props.answers.policyHolder : props.answers.holder
  )

  const currentInsuredUser = currentInsured.userDetails
  const currentInsuredDocument = currentInsured.documentDetails
  const currentInsuredAddress = currentInsured.addressDetails

  const birthDate = ref(currentInsuredUser.birthDate)
  const country = ref()
  const documentType = ref(currentInsuredDocument.documentType)
  const documentNumber = ref(currentInsuredDocument.documentNumber)
  const expirationDate = ref(currentInsuredDocument.documentExpirationDate)
  const documentsData = ref<any[]>([])
  const countries = ref<OptionsValuesInterface[]>([])
  const documents = ref<OptionsValuesInterface[]>([])
  const privacyPolicyConsent = ref(currentInsured.privacyPolicyConsent)
  const declaration = ref(currentInsured.declaration ?? false)
  const showCheck = computed(() => {
    return (
      props.extra.notPolicyHolder ||
      (props.extra.showDeclaration && props.answers.currentInsured.isFirstInsured)
    )
  })
  const message = computed(() => {
    if (currentInsured.role !== UserRoleEnum.INSURED) {
      return props.configurations.tags.holderAlert
    } else {
      return false
    }
  })
  const documentNumberLabel = computed(() => {
    return documents.value.find((d) => d.value === documentType.value)?.label
  })
  const documentNumberRules = computed(() => DOCUMENTS[documentType.value])
  const insuredYoungerThan14 = computed(() => {
    if (!birthDate.value) return false

    return isSmaller(birthDate.value, 14, props.answers.startDate)
  })
  const isHolder = computed(() => currentInsured.isHolder)
  const availableDocuments = computed(() => {
    const discardedDocuments: string[] = []
    if (country.value === SPANISH_NATIONALITY) {
      discardedDocuments.push('NIE')
      discardedDocuments.push('PAS')
    } /*else {
    discardedDocuments.push('CIF')
  }*/
    if (country.value !== SPANISH_NATIONALITY) {
      discardedDocuments.push('NIF')
    }

    if (
      !insuredYoungerThan14.value ||
      isHolder.value ||
      getBranch() === SpanishBranches.travel ||
      (getBranch() === SpanishBranches.health && props.answers.product.insuranceType === 3)
    ) {
      discardedDocuments.push('MENOR')
    }

    return documents.value.filter((d) => !discardedDocuments.includes(d.value))
  })

  const currentHiringAge = hiringAges(props.configurations?.hiringMaxAges, currentInsured?.role)

  const minAgeValue = hiringMinAge(props.extra, currentHiringAge, props.configurations)

  const maxAgeValue = hiringMaxAge(props.extra, currentHiringAge, props.configurations)

  const validationRules = {
    required,
    validNIF,
    validNIE,
    validCIF,
    validateDate,
    checkRequired: (value: boolean) => required(value, 'common.validations.checked'),
    tooYoung: (value: string) =>
      tooYoung(
        value,
        minAgeValue,
        null,
        props.extra?.minAgeErrorMsg ?? 'common.validations.min-age'
      ),
    tooOld: (value: string) =>
      props.extra?.tooOldGreaterOrEqual
        ? tooOldGreaterOrEqual(
            value,
            maxAgeValue,
            isHolder.value
              ? `common.validations.max-age-${getBranchMaxAge()}`
              : 'common.validations.date'
          )
        : tooOld(
            value,
            maxAgeValue,
            null,
            isHolder.value
              ? `common.validations.max-age-${getBranchMaxAge()}`
              : 'common.validations.date'
          ),
    characterLimit: (value: string) =>
      value.length === MAX_CHARS_PASSPORT ||
      (currentInsured.role === UserRoleEnum.INSURED
        ? t('common.validations.passport-error-not-holder')
        : t('common.validations.passport-error-holder')),
    validPassport: (value: string) => validPassport(value, 'common.validations.invalid-characters'),
    isNumberDuplicated: (value: string) =>
      props.answers.isDocumentNumberAvailable(
        value,
        currentInsured.riskNumber,
        currentInsured.role
      ) || t('common.validations.card-repeat'),
    validateMinDate: (value: string) => dateHigherOrEquals(value, props.answers.startDate)
  }

  const DOCUMENTS: Record<string, any> = Object.freeze({
    NIF: [validationRules.required, validationRules.validNIF, validationRules.isNumberDuplicated],
    NIE: [validationRules.required, validationRules.validNIE, validationRules.isNumberDuplicated],
    PAS: [
      validationRules.required,
      validationRules.validPassport,
      validationRules.characterLimit,
      validationRules.isNumberDuplicated
    ],
    TRE: [validationRules.required, validationRules.isNumberDuplicated],
    CIF: [validationRules.required, validationRules.validCIF, validationRules.isNumberDuplicated],
    CIE: [validationRules.required, validationRules.isNumberDuplicated]
  })

  watch(
    () => country.value,
    (newCountry) => {
      if (availableDocuments.value?.length > 0) {
        if (newCountry === SPANISH_NATIONALITY) {
          documentType.value =
            availableDocuments.value.find((v) => v.value === 'NIF')?.value ||
            availableDocuments.value[0].value
        } else {
          documentType.value =
            availableDocuments.value.find((v) => v.value === 'PAS')?.value ||
            availableDocuments.value[0].value
        }
      } else {
        documentType.value = ''
      }
    }
  )

  function findPersonType() {
    try {
      const document = documentsData.value.find((d) => d.documentType === documentType.value)

      if (!document) {
        throw new Error('Document not found')
      }

      return document.personType
    } catch (error) {
      console.error(error)
      return null
    }
  }

  function calculateNextPage() {
    if (props.nextForm.length > 1 && currentInsured.role !== UserRoleEnum.HOLDER_NOT_INSURED) {
      return props.nextForm[1]
    }
    return props.nextForm[0]
  }

  function triggerSubmit() {
    const page = calculateNextPage()

    if (needsBirthdate.value) {
      currentInsured.userDetails.birthDate = birthDate.value
    }

    currentInsured.addressDetails.country = country.value
    currentInsured.documentDetails.documentType = documentType.value
    currentInsured.documentDetails.documentTypeText = documentNumberLabel.value!
    currentInsured.documentDetails.documentNumber = documentNumber.value
      ? documentNumber.value.toUpperCase()
      : null
    //167195
    currentInsured.documentDetails.personType = findPersonType()

    currentInsured.documentDetails.documentExpirationDate = null
    currentInsured.privacyPolicyConsent = true

    if (documentType.value === 'PAS') {
      currentInsured.documentDetails.documentExpirationDate = expirationDate.value
    }

    if (props.extra?.notPolicyHolder) {
      currentInsured.declaration = declaration.value
    }

    const answers = props.extra?.customInsuredProp
      ? { [props.extra.customInsuredProp]: currentInsured }
      : props.extra?.currentInsuredIndex === 'holder'
      ? { holder: currentInsured }
      : currentInsured
    return { page, answers }
  }

  async function fetchDocumentTypes() {
    documentsData.value = await commonStore.fetchDocumentsByRole(currentInsured.role)

    return documentsData.value.map((d) => ({
      label: d.documentDescription,
      value: d.documentType
    }))
  }

  async function beforeMount() {
    loadingStore.startLoading()
    countries.value = await commonStore.fetchCountries()
    country.value = currentInsuredAddress.country ?? SPANISH_NATIONALITY
    documents.value = await fetchDocumentTypes()
    if (availableDocuments.value?.length > 0) {
      if (country.value === SPANISH_NATIONALITY) {
        documentType.value =
          availableDocuments.value.find((v) => v.value === 'NIF')?.value ||
          availableDocuments.value[0].value
      } else {
        documentType.value =
          availableDocuments.value.find((v) => v.value === 'PAS')?.value ||
          availableDocuments.value[0].value
      }
    } else {
      documentType.value = ''
    }
    watch(
      () => documentType.value,
      () => (documentNumber.value = null)
    )
    loadingStore.stopLoading()
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .insurance-another-company-data {
    .gl_form-title.small {
      margin-top: 0;
    }

    :deep(span.info) {
      color: $color-neutral;
      font-size: $font-size-s;
    }

    .expirationDate {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-s;
    }

    .birthdate-form-field {
      margin-bottom: $spacing-28;
    }
  }
</style>
